import { formatDateTime } from '@/utils/format'
import {
  businessQueryStatusLabel,
  businessQueryStatusTone,
  normalizeBusinessQueryStatus,
} from '@/utils/businessQueries'
import { resolveCallRecordingS3Key } from '@/utils/callRecording'

/** Format call duration for display. */
export function formatCallDuration(seconds) {
  const total = Number(seconds)
  if (!Number.isFinite(total) || total <= 0) return ''
  const mins = Math.floor(total / 60)
  const secs = Math.round(total % 60)
  return mins ? `${mins}m ${secs}s` : `${secs}s`
}

function normalizeTranscript(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((line) => {
        if (line == null) return ''
        if (typeof line === 'string') return line.trim()
        if (typeof line === 'object') {
          const speaker = String(line.speaker ?? line.role ?? '').trim()
          const text = String(line.text ?? line.content ?? line.message ?? '').trim()
          if (speaker && text) return `${speaker}: ${text}`
          return text || speaker
        }
        return String(line).trim()
      })
      .filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()]
  return []
}

function toEpochMs(value) {
  if (value == null || value === '') return 0
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value
  }
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function extractList(raw) {
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw?.items)) return raw.items
  if (Array.isArray(raw?.data?.items)) return raw.data.items
  if (Array.isArray(raw?.data)) return raw.data
  return []
}

/** Parse talk duration from getLeadHistory rows (API field or comments text). */
export function parseHistoryDurationSeconds(item = {}) {
  const direct = Number(
    item.duration_seconds ?? item.durationSeconds ?? item.duration ?? 0,
  )
  if (Number.isFinite(direct) && direct > 0) return direct

  const comments = String(item.comments ?? item.comment ?? '').trim()
  const match = comments.match(/duration:\s*(\d+)\s*s/i)
  if (match) {
    const parsed = Number(match[1])
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return 0
}

// generated_by: "SYSTEM" covers two different things that happen to share
// one write path: an actual AI-dialer call outcome, and unrelated
// automated bookkeeping (e.g. logMissedCycle closing a reminder cycle with
// no call attached). Only the first is the AI agent talking to someone --
// a row tied to a real call always carries a call_uuid, a bookkeeping note
// never does, so that's the signal used to tell them apart.
function resolveHistoryActorLabel(item = {}) {
  const generatedBy = String(item.generated_by ?? item.generatedBy ?? '')
    .trim()
    .toUpperCase()
  const agentName = String(item.agent_name ?? item.agentName ?? '').trim()
  const agentEmail = String(item.agent_email ?? item.agentEmail ?? '').trim()
  const agentLabel =
    (agentName && !/^[0-9a-f-]{36}$/i.test(agentName) ? agentName : '') ||
    (agentEmail && !/^[0-9a-f-]{36}$/i.test(agentEmail) ? agentEmail : '')
  const hasCallUuid = Boolean(String(item.call_uuid ?? item.callUuid ?? '').trim())

  if (generatedBy === 'AGENT') {
    return agentLabel ? `AGENT · ${agentLabel}` : 'AGENT'
  }
  if (generatedBy === 'SYSTEM') {
    return hasCallUuid ? 'AI Agent' : 'SYSTEM'
  }
  if (generatedBy === 'AI_AGENT') {
    return 'AI Agent'
  }
  if (generatedBy) {
    return agentLabel ? `${generatedBy} · ${agentLabel}` : generatedBy
  }

  // Legacy rows without generated_by
  const performedBy = String(item.performed_by ?? item.performedBy ?? '')
    .trim()
    .toUpperCase()
  if (performedBy === 'AI_AGENT') {
    return 'AI Agent'
  }
  if (performedBy === 'SYSTEM') {
    return hasCallUuid ? 'AI Agent' : 'SYSTEM'
  }
  if (agentLabel) return `AGENT · ${agentLabel}`
  return ''
}

function isCallHistoryEntry(item = {}) {
  if (resolveCallRecordingS3Key(item)) return true
  return /call completed/i.test(String(item.comments ?? item.comment ?? ''))
}

export function normalizeLeadCallHistoryItem(item, index = 0) {
  if (!item || typeof item !== 'object') return null
  const calledOn =
    item.called_on ??
    item.calledOn ??
    item.started_on ??
    item.startedOn ??
    item.created_on ??
    item.createdOn ??
    null
  const durationSeconds = Number(
    item.duration_seconds ?? item.durationSeconds ?? item.duration ?? 0,
  )
  const direction = String(
    item.call_direction ?? item.callDirection ?? item.direction ?? '',
  )
    .trim()
    .toUpperCase()
  const status = String(item.call_status ?? item.callStatus ?? item.status ?? '').trim()
  const recordingUrl = String(
    item.recording_url ?? item.recordingUrl ?? '',
  ).trim()
  const callUuid = String(item.call_uuid ?? item.callUuid ?? '').trim()
  const requestStatus = normalizeBusinessQueryStatus(
    item.request_status ?? item.requestStatus ?? '',
  )
  const comments = String(item.comments ?? item.comment ?? '').trim()

  return {
    id: callUuid || `call-${index}`,
    callUuid,
    direction,
    directionLabel: direction || '—',
    status,
    calledOn,
    calledOnMs: toEpochMs(calledOn),
    calledOnLabel: calledOn ? formatDateTime(calledOn) : '—',
    durationSeconds: Number.isFinite(durationSeconds) ? durationSeconds : 0,
    durationLabel: formatCallDuration(durationSeconds),
    transcript: normalizeTranscript(item.transcript),
    recordingUrl,
    comments,
    requestStatus,
    requestStatusLabel: requestStatus
      ? businessQueryStatusLabel(requestStatus)
      : '',
    requestStatusTone: requestStatus
      ? businessQueryStatusTone(requestStatus) || 'neutral'
      : 'neutral',
  }
}

export function normalizeLeadCallHistoryList(raw) {
  return extractList(raw)
    .map((item, index) => normalizeLeadCallHistoryItem(item, index))
    .filter(Boolean)
}

/** Normalize one getLeadHistory item for UI + interest-score metrics. */
export function normalizeLeadStatusHistoryItem(item, index = 0) {
  if (!item || typeof item !== 'object') return null
  const status = normalizeBusinessQueryStatus(
    item.request_status ?? item.requestStatus,
  )
  const createdOn =
    item.created_on ?? item.createdOn ?? item.updated_on ?? item.updatedOn ?? null
  const callUuid = String(item.call_uuid ?? item.callUuid ?? '').trim()
  const recordingS3Key = resolveCallRecordingS3Key(item)
  const recordingUrl = String(
    item.recording_s3_url ??
      item.recordingS3Url ??
      item.recording_url ??
      item.recordingUrl ??
      '',
  ).trim()
  const durationSeconds = parseHistoryDurationSeconds(item)
  const rawScore = item.lead_score ?? item.leadScore
  const leadScore =
    rawScore == null || rawScore === '' || Number.isNaN(Number(rawScore))
      ? null
      : Math.max(0, Math.min(100, Number(rawScore)))
  const comments = String(item.comments ?? item.comment ?? '').trim()
  const isCall = isCallHistoryEntry(item)
  const generatedBy = String(item.generated_by ?? item.generatedBy ?? '')
    .trim()
    .toUpperCase()
  const agentName = String(item.agent_name ?? item.agentName ?? '').trim()
  const agentEmail = String(item.agent_email ?? item.agentEmail ?? '').trim()
  const actorLabel = resolveHistoryActorLabel(item)
  const callbackRequestedOn = item.callback_requested_on ?? item.callbackRequestedOn ?? null
  const callbackNote = String(item.callback_note ?? item.callbackNote ?? '').trim()

  return {
    id: String(item.history_id ?? item.historyId ?? `status-${index}`),
    callUuid,
    callbackRequestedOn,
    callbackRequestedOnLabel: callbackRequestedOn ? formatDateTime(callbackRequestedOn) : '',
    callbackNote,
    status,
    statusLabel: businessQueryStatusLabel(status),
    tone: businessQueryStatusTone(status) || 'neutral',
    comments,
    performedBy: String(item.performed_by ?? item.performedBy ?? '').trim(),
    generatedBy,
    agentName,
    agentEmail,
    actorLabel,
    createdOn,
    createdOnMs: toEpochMs(createdOn),
    createdOnLabel: createdOn ? formatDateTime(createdOn) : '',
    recordingS3Key: recordingS3Key || '',
    recordingUrl,
    hasRecording: Boolean(recordingS3Key),
    durationSeconds,
    durationLabel: formatCallDuration(durationSeconds),
    leadScore,
    isCall,
  }
}

export function normalizeLeadStatusHistoryList(raw) {
  return extractList(raw)
    .map((item, index) => normalizeLeadStatusHistoryItem(item, index))
    .filter(Boolean)
    .sort((a, b) => (b.createdOnMs || 0) - (a.createdOnMs || 0))
}

/**
 * Build activity cards from getLeadHistory only (no getLeadCallHistory).
 */
export function buildLeadActivityFeedFromHistory(entries = []) {
  const list = Array.isArray(entries) ? entries.filter(Boolean) : []
  return list.map((entry) => ({
    kind: entry.isCall ? 'call' : 'status',
    id: `${entry.isCall ? 'call' : 'status'}-${entry.id}`,
    callUuid: entry.callUuid || '',
    atMs: entry.createdOnMs || 0,
    atLabel: entry.createdOnLabel || '—',
    callStatus: entry.status,
    callStatusLabel: entry.statusLabel || entry.status || '',
    durationLabel: entry.durationLabel || '',
    recordingS3Key: entry.recordingS3Key || '',
    recordingUrl: entry.recordingUrl || '',
    hasRecording: Boolean(entry.hasRecording || entry.recordingS3Key),
    recordingSource: entry.hasRecording || entry.recordingS3Key
      ? {
          recording_s3_key: entry.recordingS3Key || undefined,
          recording_s3_url: entry.recordingUrl || undefined,
        }
      : null,
    transcript: [],
    requestStatus: entry.status,
    requestStatusLabel: entry.statusLabel,
    requestStatusTone: entry.tone || 'neutral',
    comments: entry.comments,
    performedBy: entry.performedBy,
    generatedBy: entry.generatedBy || '',
    actorLabel: entry.actorLabel || '',
    callbackRequestedOn: entry.callbackRequestedOn || null,
    callbackRequestedOnLabel: entry.callbackRequestedOnLabel || '',
    callbackNote: entry.callbackNote || '',
    statusUpdates: [entry],
  }))
}

/**
 * Interest score chip metrics from getLeadHistory rows.
 */
export function computeLeadHistoryMetrics(history = [], lead = null) {
  const list = Array.isArray(history) ? history.filter(Boolean) : []
  const totalSeconds = list.reduce(
    (sum, row) => sum + (Number(row.durationSeconds) || 0),
    0,
  )
  const latest = list[0] || null
  const latestScored = list.find((row) => row.leadScore != null) || null
  const score =
    latestScored?.leadScore != null
      ? latestScored.leadScore
      : lead?.leadScore != null
        ? lead.leadScore
        : null

  return {
    totalCalls: list.length,
    talkTimeLabel: totalSeconds > 0 ? formatCallDuration(totalSeconds) : '—',
    lastCallStatusLabel: latest?.statusLabel || latest?.status || '—',
    score,
  }
}

/**
 * Merge getLeadCallHistory (recordings) with getLeadHistory (status/comments).
 *
 * - Calls always appear when a recording/call row exists (remarks optional).
 * - Status rows appear only when explicitly submitted.
 * - Join key: call_uuid (attached to history only if client passed it into
 *   updateMyTaskLeadStatus). Unlinked status rows stay as separate cards.
 * - Both sources are also keyed by request_id; call history may be scoped
 *   by task_id for the active campaign.
 */
export function buildLeadActivityFeed(calls = [], statusEntries = []) {
  const callList = Array.isArray(calls) ? calls.filter(Boolean) : []
  const statusList = Array.isArray(statusEntries) ? statusEntries.filter(Boolean) : []

  const byCallUuid = new Map()
  const unlinked = []

  for (const entry of statusList) {
    const key = String(entry.callUuid || '').trim()
    if (key) {
      const bucket = byCallUuid.get(key) || []
      bucket.push(entry)
      byCallUuid.set(key, bucket)
    } else {
      unlinked.push(entry)
    }
  }

  const feed = []

  for (const call of callList) {
    const linked = byCallUuid.get(call.callUuid) || []
    if (call.callUuid) byCallUuid.delete(call.callUuid)

    const primary = linked[0] || null
    const comments =
      call.comments ||
      linked
        .map((entry) => entry.comments)
        .filter(Boolean)
        .join('\n') ||
      ''

    feed.push({
      kind: 'call',
      id: `call-${call.id}`,
      callUuid: call.callUuid || '',
      atMs: call.calledOnMs || linked[0]?.createdOnMs || 0,
      atLabel: call.calledOnLabel,
      callStatus: call.status,
      callStatusLabel: call.status || call.directionLabel || '—',
      durationLabel: call.durationLabel,
      recordingUrl: call.recordingUrl,
      transcript: call.transcript,
      requestStatus: primary?.status || call.requestStatus || '',
      requestStatusLabel:
        primary?.statusLabel || call.requestStatusLabel || '',
      requestStatusTone:
        primary?.tone || call.requestStatusTone || 'neutral',
      comments,
      performedBy: primary?.performedBy || '',
      generatedBy: primary?.generatedBy || '',
      actorLabel: primary?.actorLabel || '',
      callbackRequestedOn: primary?.callbackRequestedOn || null,
      callbackRequestedOnLabel: primary?.callbackRequestedOnLabel || '',
      callbackNote: primary?.callbackNote || '',
      statusUpdates: linked,
    })
  }

  for (const leftover of byCallUuid.values()) {
    unlinked.push(...leftover)
  }

  for (const entry of unlinked) {
    feed.push({
      kind: 'status',
      id: `status-${entry.id}`,
      callUuid: entry.callUuid || '',
      atMs: entry.createdOnMs || 0,
      atLabel: entry.createdOnLabel || '—',
      callStatus: '',
      callStatusLabel: '',
      durationLabel: '',
      recordingUrl: '',
      transcript: [],
      requestStatus: entry.status,
      requestStatusLabel: entry.statusLabel,
      requestStatusTone: entry.tone || 'neutral',
      comments: entry.comments,
      performedBy: entry.performedBy,
      generatedBy: entry.generatedBy || '',
      actorLabel: entry.actorLabel || '',
      callbackRequestedOn: entry.callbackRequestedOn || null,
      callbackRequestedOnLabel: entry.callbackRequestedOnLabel || '',
      callbackNote: entry.callbackNote || '',
      statusUpdates: [entry],
    })
  }

  feed.sort((a, b) => (b.atMs || 0) - (a.atMs || 0))
  return feed
}

/** Normalize generateLeadComments mutation payload. */
export function normalizeGenerateLeadComments(response) {
  const root = response && typeof response === 'object' ? response : {}
  const data =
    root.data && typeof root.data === 'object' && !Array.isArray(root.data)
      ? root.data
      : root

  const available =
    data.available === true ||
    (data.available !== false &&
      Boolean(
        String(data.comments ?? data.comment ?? data.suggested_comments ?? '').trim() ||
          String(data.request_status ?? data.requestStatus ?? '').trim() ||
          data.lead_score != null ||
          data.leadScore != null,
      ))

  const rawScore = data.lead_score ?? data.leadScore
  const leadScore =
    rawScore == null || rawScore === '' || Number.isNaN(Number(rawScore))
      ? null
      : Math.max(0, Math.min(100, Number(rawScore)))

  const bestTimeRaw =
    data.best_time_to_call ?? data.bestTimeToCall ?? null
  const bestTimeToCall =
    bestTimeRaw == null || bestTimeRaw === ''
      ? null
      : Number(bestTimeRaw)

  return {
    available,
    message: String(root.message ?? data.message ?? '').trim(),
    comments: String(
      data.comments ?? data.comment ?? data.suggested_comments ?? '',
    ).trim(),
    requestStatus: String(
      data.request_status ?? data.requestStatus ?? data.suggested_status ?? '',
    )
      .trim()
      .toUpperCase(),
    leadScore,
    leadOverview: String(
      data.lead_overview ?? data.leadOverview ?? '',
    ).trim(),
    bestTimeToCall:
      Number.isFinite(bestTimeToCall) && bestTimeToCall > 0
        ? bestTimeToCall
        : null,
  }
}
